import firebase from "@/plugins/firebase";
import { ShoppingCart, ShoppingCartItem, ShoppingCartItemType } from "@/types";
import router from "@/router";
import Vue from "vue";

const ecommerce = {
  namespaced: true,

  state: {
    shoppingCart: {} as ShoppingCart,
    shoppingCartStatus: true
  },

  getters: {
    shoppingCart(state: any) {
      return state.shoppingCart;
    },
    cartItemCount(state: any) {
      return state.shoppingCart.items ? state.shoppingCart.items.length : 0;
    },
    shoppingCartStatus(state: any) {
      return state.shoppingCartStatus;
    }
  },

  mutations: {
    setShoppingCart(state: any, cart: ShoppingCart) {
      state.shoppingCart = cart;
    },
    setShoppingCartStatus(state: any, value: Boolean) {
      state.shoppingCartStatus = value;
    }
  },

  actions: {
    // Get current user's shopping cart
    async getShoppingCart(context: any, uid: string) {
      let newCart: ShoppingCart;

      const cartQs = await firebase
        .firestore()
        .collection("shoppingCarts")
        .where("uid", "==", uid)
        .get();

      if (cartQs.docs.length !== 0) {
        const cart = cartQs.docs[0];

        newCart = {
          id: cart.id,
          amount: cart.data().amount,
          items: [],
          creationTime: cart.data().creationTime.toDate(),
          updateTime: cart.data().updateTime
            ? cart.data().updateTime.toDate()
            : null
        };

        const cartItems = cart.data().items;
        // Get items infos in the cart
        cartItems.forEach(async (item: any) => {
          if (item.type === ShoppingCartItemType.Course) {
            const course = await firebase
              .firestore()
              .collection("courses")
              .doc(item.id)
              .get();

            if (course.exists) {
              let newCartItem = {
                id: item.id,
                type: "course",
                name: course.get("name"),
                previewImg: course.get("previewImg"),
                price: course.get("price"),
                creationTime: item.creationTime.toDate()
              } as ShoppingCartItem;

              newCart.items.push(newCartItem);
            }
          }
        });

        context.commit("setShoppingCart", newCart);
      }
    },

    // Add new item to shopping cart
    async addItemToCart(
      context: any,
      item: { id: string; name: string; type: string }
    ) {
      // Check user is signed in.
      if (!context.rootGetters["auth/userSignedIn"]) {
        // Show message: Sign-in
        Vue.notify({
          text: "Alış-veriş yapabilmek için lütfen giriş yapınız."
        });

        setTimeout(() => {
          router.push(
            {
              name: "signin",
              query: { returnUrl: router.currentRoute.path }
            },
            () => {}
          );
        }, 2000);

        return;
      }

      const cart: ShoppingCart = context.getters.shoppingCart;

      // Check if item already exists.
      let itemExists = false;
      if (cart.items) {
        itemExists = cart.items.some(i => i.id === item.id);
      }

      if (itemExists) {
        // If item is already in cart show message
        Vue.notify({
          type: "error",
          text: `${item.name} zaten sepetinizde var.`
        });
      } else {
        try {
          // Add item to database
          const addItemToCartFunc = firebase
            .functions()
            .httpsCallable("ecommerce-addItemToCart");

          const response = await addItemToCartFunc({
            uid: context.rootGetters["auth/user"].id,
            item: item
          });

          // Update cart in app cache
          await context.commit("setShoppingCart", response.data);

          // Show success message
          Vue.notify({
            type: "success",
            text: `${item.name} sepete eklendi.`
          });
        } catch (e) {
          // Show error message
          Vue.notify({
            type: "error",
            text: `HATA: ${item.name} sepete eklenemedi.`
          });

          throw e;
        }
      }
    },

    async removeItemFromCart(
      context: any,
      item: ShoppingCartItem
    ): Promise<void> {
      const cart: ShoppingCart = context.getters.shoppingCart;

      try {
        // Delete item from cart in cache
        cart.items.forEach((i: ShoppingCartItem, index: number) => {
          if (i.id === item.id) {
            // Calculate new cart amount
            cart.amount = cart.amount - cart.items[index].price;
            // Delete item from cart
            cart.items.splice(index, 1);
            // Update cart in app cache
            context.commit("setShoppingCart", cart);

            // Update database
            const cartRef = firebase
              .firestore()
              .collection("shoppingCarts")
              .doc(cart.id);

            cartRef.update({
              amount: cart.amount,
              items: cart.items,
              updateTime: firebase.firestore.FieldValue.serverTimestamp()
            });
          }
        });

        Vue.notify({
          type: "success",
          text: `${item.name} sepetten çıkarıldı.`
        });
      } catch (e) {
        // Show error message
        Vue.notify({
          type: "error",
          text: `HATA: ${item.name} sepetten çıkarılamadı.`
        });

        throw e;
      }
    },

    async deleteShoppingCart(context: any) {
      await firebase
        .firestore()
        .doc(
          `/shoppingCarts/${context.rootGetters["ecommerce/shoppingCart"].id}`
        )
        .delete();
    }
  }
};

export default ecommerce;
